import { useTransactionStore } from '@/stores/transactionStore'
import type { TransactionRecord, TransactionItem } from '@/types/Transaction'
import { localForageService } from '@/utils/cache'
import { createTransaction } from '@/factories/transactionFactory'
import type { Person } from '@/types/Person'
import type { Vehicle } from '@/types/Vehicle'
import { TransactionModel } from '@/models/TransactionModel'

/**
 * Transaction Service - Provides business logic for transaction-related operations
 * Uses the transaction store for state management and persistence
 */
export function useTransactionService() {
  const transactionStore = useTransactionStore()

  return {
    /**
     * Start editing a transaction
     * Creates a draft copy and stores it in local storage
     */
    async startEditing(id: string): Promise<TransactionRecord> {
      await transactionStore.update(id, {
        transaction_status: 'editing',
      })

      const cacheKey = `transaction_draft_${id}`
      const transaction = await transactionStore.get(id)
      if (!transaction) throw new Error('Transaction not found')

      // Store draft in local storage
      await localForageService.set(cacheKey, transaction)
      return transaction
    },

    /**
     * Cancel editing a transaction
     * Restores original status and removes draft from local storage
     */
    async cancelEditing(id: string): Promise<void> {
      const transaction = await transactionStore.get(id)
      if (!transaction) throw new Error('Transaction not found')

      const originalStatus = transaction.payment_status || 'pending'
      await transactionStore.update(id, {
        transaction_status: originalStatus,
      })

      await localForageService.remove(`transaction_draft_${id}`)
    },

    /**
     * Finalize a transaction
     * Checks all requirements are met and updates statuses
     */
    async finalizeTransaction(id: string): Promise<void> {
      const transaction = await transactionStore.get(id)
      if (!transaction) throw new Error('Transaction not found')

      // Check if all items have images
      const itemsMissingImages =
        transaction.items?.filter((item: TransactionItem) => !item.image_url) ?? []

      if (itemsMissingImages.length > 0) {
        const missingCount = itemsMissingImages.length
        const itemText = missingCount === 1 ? 'item is' : 'items are'
        throw new Error(
          `${missingCount} ${itemText} missing images. All items must have images to finalize the transaction.`,
        )
      }

      await transactionStore.update(id, {
        payment_status: 'completed',
        transaction_status: 'completed',
        updated_at: new Date(),
      })
    },

    /**
     * Get transactions by person ID
     */
    async getTransactionsByPersonId(personId: string): Promise<TransactionRecord[]> {
      await transactionStore.load()
      return transactionStore.items.filter(
        (transaction: TransactionRecord) => transaction.personId === personId,
      )
    },

    /**
     * Get transactions by organization ID
     */
    async getTransactionsByOrganizationId(organizationId: string): Promise<TransactionRecord[]> {
      await transactionStore.load()
      return transactionStore.items.filter(
        (transaction: TransactionRecord) => transaction.organizationId === organizationId,
      )
    },

    /**
     * Get purchase transactions within a date range
     */
    async getPurchaseTransactionsByDateRange(
      startDate: Date,
      endDate: Date,
    ): Promise<TransactionRecord[]> {
      await transactionStore.load()
      return transactionStore.items.filter((transaction: TransactionRecord) => {
        return (
          transaction.transaction_type === 'purchase' &&
          transaction.transaction_date >= startDate &&
          transaction.transaction_date <= endDate
        )
      })
    },

    /**
     * Create a new transaction with validation
     */
    async createTransaction(data: Partial<TransactionRecord>): Promise<TransactionRecord> {
      const transaction = createTransaction(data)
      return await transactionStore.add(transaction)
    },

    /**
     * Update transaction items
     */
    async updateTransactionItems(id: string, items: TransactionItem[]): Promise<void> {
      await transactionStore.update(id, {
        items,
        updated_at: new Date(),
      })
    },

    /**
     * Get transaction by ID with draft handling
     */
    async getTransactionWithDraft(id: string): Promise<{
      transaction: TransactionRecord | null
      draft: TransactionRecord | null
    }> {
      const transaction = await transactionStore.get(id)
      const draft = await localForageService.get<TransactionRecord>(`transaction_draft_${id}`)

      return {
        transaction: transaction || null,
        draft: draft || null,
      }
    },

    /**
     * Save draft changes to the transaction
     */
    async saveDraftChanges(id: string, draft: TransactionRecord): Promise<void> {
      await transactionStore.update(id, {
        ...draft,
        transaction_status: draft.payment_status || 'pending',
        updated_at: new Date(),
      })

      await localForageService.remove(`transaction_draft_${id}`)
    },

    /**
     * Get quick price estimate for a commodity based on recent transactions
     * Returns the lowest price found in the last 30 days for the given commodity
     */
    async getQuickPriceEstimate(commodityId: string): Promise<number | null> {
      await transactionStore.load()

      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const recentTransactions = transactionStore.items.filter((transaction: TransactionRecord) => {
        return (
          transaction.transaction_date >= thirtyDaysAgo &&
          transaction.items?.some((item: TransactionItem) => item.commodity_type_id === commodityId)
        )
      })

      if (recentTransactions.length === 0) {
        return null
      }

      let lowestPrice = Infinity

      for (const transaction of recentTransactions) {
        if (transaction.items) {
          for (const item of transaction.items) {
            if (
              item.commodity_type_id === commodityId &&
              item.price_per_lb &&
              item.price_per_lb > 0
            ) {
              lowestPrice = Math.min(lowestPrice, item.price_per_lb)
            }
          }
        }
      }

      return lowestPrice === Infinity ? null : lowestPrice
    },

    /**
     * Validate transaction for submission
     */
    validateTransactionForSubmission(
      transaction: TransactionRecord,
      person?: Person,
      vehicle?: Vehicle,
      hasIdPhoto: boolean = false,
    ): {
      isValid: boolean
      errors: string[]
    } {
      const errors: string[] = []

      // Check person/company selection
      if (!transaction.personId && !transaction.organizationId) {
        errors.push('Select a person or company')
      }

      // Check person validation if person is selected
      if (transaction.personId && person) {
        if (!person.firstName?.trim()) {
          errors.push('First name is required')
        }
        if (!person.lastName?.trim()) {
          errors.push('Last name is required')
        }
        if (!hasIdPhoto) {
          errors.push('ID photo is required')
        }
      }

      // Check vehicle validation
      if (vehicle) {
        const hasVin = !!vehicle.vin
        const hasTag = !!vehicle.tagNumber
        const hasState = !!vehicle.state

        if (!hasVin) {
          if (!hasTag) {
            errors.push('Tag number is required when VIN is missing')
          }
          if (!hasState) {
            errors.push('State is required when VIN is missing')
          }
        }

        if (!vehicle.make) {
          errors.push('Make is required')
        }
        if (!vehicle.model) {
          errors.push('Model is required')
        }
        if (!vehicle.color) {
          errors.push('Color is required')
        }
      }

      // Check items validation
      if (!transaction.items || transaction.items.length === 0) {
        errors.push('At least one item is required')
      } else {
        // Validate each item
        transaction.items.forEach((item, index) => {
          if (!item.commodity_type_id) {
            errors.push(`Item ${index + 1}: Commodity type is required`)
          }
          if (!item.weight?.net || item.weight.net <= 0) {
            errors.push(`Item ${index + 1}: Weight is required and must be greater than 0`)
          }
          if (!item.price_per_lb || item.price_per_lb <= 0) {
            errors.push(`Item ${index + 1}: Price per pound is required and must be greater than 0`)
          }
        })
      }

      // Check signature
      if (!transaction.party_signature?.data_url) {
        errors.push('Supplier signature is missing')
      }

      return {
        isValid: errors.length === 0,
        errors,
      }
    },

    /**
     * Submit a transaction with validation
     */
    async submitTransaction(
      transactionId: string,
      person?: Person,
      vehicle?: Vehicle,
      hasIdPhoto: boolean = false,
    ): Promise<TransactionRecord> {
      const transaction = await transactionStore.get(transactionId)
      if (!transaction) {
        throw new Error('Transaction not found')
      }

      const validation = this.validateTransactionForSubmission(
        transaction,
        person,
        vehicle,
        hasIdPhoto,
      )

      if (!validation.isValid) {
        throw new Error(`Transaction validation failed: ${validation.errors.join(', ')}`)
      }

      // Update transaction status to submitted
      await transactionStore.update(transactionId, {
        transaction_status: 'submitted',
        updated_at: new Date(),
      })

      const updatedTransaction = await transactionStore.get(transactionId)
      if (!updatedTransaction) {
        throw new Error('Transaction not found after update')
      }
      return updatedTransaction
    },

    /**
     * Get transaction summary for display
     */
    getTransactionSummary(transaction: TransactionRecord): {
      totalItems: number
      totalWeight: number
      totalAmount: string
    } {
      const model = new TransactionModel(transaction)
      const totalAmountStr =
        typeof model.totalAmount === 'string' ? model.totalAmount : String(model.totalAmount)
      return {
        totalItems: (transaction.items || []).length,
        totalWeight: parseFloat(model.totalWeight) || 0,
        totalAmount: TransactionModel.formatCurrency(parseFloat(totalAmountStr || '0')),
      }
    },

    /**
     * Check if transaction is ready for submission
     */
    isTransactionReadyForSubmission(
      transaction: TransactionRecord,
      person?: Person,
      vehicle?: Vehicle,
      hasIdPhoto: boolean = false,
    ): boolean {
      const validation = this.validateTransactionForSubmission(
        transaction,
        person,
        vehicle,
        hasIdPhoto,
      )
      return validation.isValid
    },
  }
}
